/**
 * Option Plus Add to Cart gate
 * ---------------------------------------------------------------------------
 * The "Option Plus" app renders one hidden <select data-title="..."> per
 * required option inside #optionPlusvariant-container, paired with its own
 * custom searchable dropdown widget (.searchable-dropdown / .dropdown-option)
 * that writes the chosen value onto the hidden select. This script never
 * touches that markup — it only reads the hidden selects' values and toggles
 * the Add to Cart button's disabled state, so it keeps working regardless of
 * how the app renders or updates its widgets.
 *
 * Handles:
 *  - Container not present yet at parse time (app embed can inject later).
 *  - Dropdowns added/removed after load (MutationObserver on the container).
 *  - The app updating the hidden <select> via a real "change" event, or via
 *    direct .value/selected assignment with no event at all (delegated click
 *    listener on .dropdown-option covers that case too).
 */
(() => {
  const CONTAINER_ID = 'optionPlusvariant-container';
  const SELECT_SELECTOR = 'select[data-title]';

  function findSubmitButton() {
    return (
      document.querySelector('product-form .product-form__submit') ||
      document.querySelector('.product-form__submit[type="submit"]') ||
      document.querySelector('.product-form__submit')
    );
  }

  function isValidSelect(select) {
    const value = select.value;
    if (!value) return false;
    const selectedOption = select.options[select.selectedIndex];
    if (selectedOption && selectedOption.disabled) return false;
    return true;
  }

  function allRequiredOptionsValid(container) {
    const selects = container.querySelectorAll(SELECT_SELECTOR);
    if (!selects.length) return true;
    return Array.from(selects).every(isValidSelect);
  }

  class OptionPlusGate {
    constructor(button) {
      this.button = button;
      this.container = null;
      this.observer = null;
      this.wasDisabledByGate = false;

      this.evaluate = this.evaluate.bind(this);
      this.attachToContainer = this.attachToContainer.bind(this);

      // Disable up front so there's no flash of an enabled button before the
      // container check resolves — reverted by the grace-period timeout in
      // watchForContainer() below if no Option Plus block ever shows up.
      this.setDisabled(true);
      this.watchForContainer();
    }

    // Products with no Option Plus block never get #optionPlusvariant-container
    // at all — give it a short grace period (in case the app injects it a beat
    // after DOMContentLoaded) and only gate the button once a container with
    // at least one required dropdown actually shows up. Otherwise the button
    // must stay in its normal, theme-controlled enabled state.
    watchForContainer() {
      const existing = document.getElementById(CONTAINER_ID);
      if (existing) {
        this.attachToContainer(existing);
        return;
      }

      const bodyObserver = new MutationObserver(() => {
        const container = document.getElementById(CONTAINER_ID);
        if (container) {
          bodyObserver.disconnect();
          clearTimeout(this.giveUpTimer);
          this.attachToContainer(container);
        }
      });
      bodyObserver.observe(document.documentElement, { childList: true, subtree: true });

      this.giveUpTimer = setTimeout(() => {
        bodyObserver.disconnect();
        if (!this.container) this.setDisabled(false);
      }, 3000);
    }

    attachToContainer(container) {
      this.container = container;

      container.addEventListener('change', (evt) => {
        if (evt.target.matches(SELECT_SELECTOR)) this.evaluate();
      });

      container.addEventListener('click', (evt) => {
        if (evt.target.closest('.dropdown-option')) {
          this.evaluate();
        }
      });

      this.observer = new MutationObserver(() => this.evaluate());
      this.observer.observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['selected', 'value'],
      });

      this.evaluate();
    }

    evaluate() {
      const valid = this.container ? allRequiredOptionsValid(this.container) : false;
      this.setDisabled(!valid);
    }

    setDisabled(disabled) {
      if (disabled) {
        this.button.setAttribute('disabled', 'disabled');
        this.button.setAttribute('aria-disabled', 'true');
        this.wasDisabledByGate = true;
      } else if (this.wasDisabledByGate) {
        this.button.removeAttribute('disabled');
        this.button.removeAttribute('aria-disabled');
        this.wasDisabledByGate = false;
      }
    }
  }

  function init() {
    const button = findSubmitButton();
    if (!button) return;

    new OptionPlusGate(button);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
